import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { InsInsightOutcomeResponse } from '@plato/data/prisma';
import Text from '@plato/ui-legacy/components/ui/text';
import {
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import { Button } from '@plato/ui/primitives/button';
import { Spinner } from '@plato/ui/primitives/spinner';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import type { ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { getInsightName } from '../../features/insights-common/lib/insight-name.ts';
import { RichTextInput } from '../../features/rich-text/components/rich-text-input.tsx';
import type { InsightDTO } from '../../server/dto/insight-dto.ts';
import { richTextSchema } from '../../server/types/common-types.ts';

const formSchema = z.object({
    response: z.enum(InsInsightOutcomeResponse),
    textRich: richTextSchema.nullable(),
});

const defaultResponse = InsInsightOutcomeResponse.INTERESTED;

export type InsightOutcomeResolvedFormValues = z.infer<typeof formSchema>;

type InsightOutcomeResolvedFormProps = {
    insight: InsightDTO;
    estimatedPotential: string;
    productCategoryTitle: string;
    /** Whether an external submit is in flight — drives the submit button's loading/disabled state. */
    isSubmitting?: boolean;
    /** Presentational form: it gathers and validates the outcome, the caller performs the submission. */
    onSubmit: (data: InsightOutcomeResolvedFormValues) => void;
    onClose: () => void;
};

export function InsightOutcomeResolvedForm({
    insight,
    estimatedPotential,
    productCategoryTitle,
    isSubmitting = false,
    onSubmit,
    onClose,
}: InsightOutcomeResolvedFormProps): ReactNode {
    const { t } = useTranslation();
    const { enableNeverBoughtCrossSellCopy } = useAppConfig();

    // A customer-wide churn warning has no merchandise group to have no need for, so the
    // description must not name one (RETAIN-813).
    const hasCategory = insight.actionable_entity_type === 'WEB_CATEGORY';

    const {
        handleSubmit,
        control,
        formState: { isValid },
    } = useForm<InsightOutcomeResolvedFormValues>({
        defaultValues: {
            response: defaultResponse,
            textRich: null,
        },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const selectedResponse = useWatch({ control, name: 'response', defaultValue: defaultResponse });

    const getPlaceholder = (): string => {
        if (selectedResponse === InsInsightOutcomeResponse.INTERESTED) {
            return t('outcome_context__placeholder_resolved__INTERESTED', { ns: 'insights' });
        }

        if (selectedResponse === InsInsightOutcomeResponse.NO_NEED) {
            return t('outcome_context__placeholder_resolved__NO_NEED', { ns: 'insights' });
        }

        return t('outcome_context__placeholder_resolved__BUYS_ELSEWHERE', { ns: 'insights' });
    };

    return (
        <>
            <DialogHeader>
                <div className="flex min-w-0 flex-1 flex-col gap-tight">
                    <DialogTitle>{t('outcome_modal__title_resolved', { ns: 'insights' })}</DialogTitle>
                    <Typography variant="body" className="text-content-weak tabular-nums">
                        {productCategoryTitle} •{' '}
                        {getInsightName(t, insight, { neverBoughtCrossSell: enableNeverBoughtCrossSellCopy })}{' '}
                        • {estimatedPotential} {t('outcome_modal__est_value', { ns: 'insights' })}
                    </Typography>
                </div>
                <DialogIconClose label={t('form__close', { ns: 'common' })} />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 bg-neutral-50 rounded-lg p-3">
                    <Text size="sm" weight="medium" color="neutral-700">
                        {t('outcome_modal__question_resolved', { ns: 'insights' })}
                    </Text>

                    <Controller
                        name="response"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-col gap-2">
                                <button
                                    key={InsInsightOutcomeResponse.INTERESTED}
                                    type="button"
                                    onClick={() => {
                                        onChange(InsInsightOutcomeResponse.INTERESTED);
                                    }}
                                    className={cn(
                                        'flex items-start gap-2 rounded-lg border border-neutral-200 p-3 text-left bg-white',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                            value === InsInsightOutcomeResponse.INTERESTED
                                                ? 'border-blue-500'
                                                : 'border-neutral-300',
                                        )}
                                    >
                                        {value === InsInsightOutcomeResponse.INTERESTED && (
                                            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                                        )}
                                    </span>
                                    <div className="flex flex-col gap-1">
                                        <Text size="sm" weight="medium" color="neutral-950">
                                            {t('outcome_response__INTERESTED', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                        <Text size="sm" color="neutral-500">
                                            {t('outcome_response__INTERESTED__description', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                    </div>
                                </button>
                                <button
                                    key={InsInsightOutcomeResponse.BUYS_ELSEWHERE}
                                    type="button"
                                    onClick={() => {
                                        onChange(InsInsightOutcomeResponse.BUYS_ELSEWHERE);
                                    }}
                                    className={cn(
                                        'flex items-start gap-2 rounded-lg border border-neutral-200 p-3 text-left bg-white',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                            value === InsInsightOutcomeResponse.BUYS_ELSEWHERE
                                                ? 'border-blue-500'
                                                : 'border-neutral-300',
                                        )}
                                    >
                                        {value === InsInsightOutcomeResponse.BUYS_ELSEWHERE && (
                                            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                                        )}
                                    </span>
                                    <div className="flex flex-col gap-1">
                                        <Text size="sm" weight="medium" color="neutral-950">
                                            {t('outcome_response__BUYS_ELSEWHERE', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                        <Text size="sm" color="neutral-500">
                                            {t('outcome_response__BUYS_ELSEWHERE__description', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                    </div>
                                </button>
                                <button
                                    key={InsInsightOutcomeResponse.NO_NEED}
                                    type="button"
                                    onClick={() => {
                                        onChange(InsInsightOutcomeResponse.NO_NEED);
                                    }}
                                    className={cn(
                                        'flex items-start gap-2 rounded-lg border border-neutral-200 p-3 text-left bg-white',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                            value === InsInsightOutcomeResponse.NO_NEED
                                                ? 'border-blue-500'
                                                : 'border-neutral-300',
                                        )}
                                    >
                                        {value === InsInsightOutcomeResponse.NO_NEED && (
                                            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                                        )}
                                    </span>
                                    <div className="flex flex-col gap-1">
                                        <Text size="sm" weight="medium" color="neutral-950">
                                            {t('outcome_response__NO_NEED', {
                                                ns: 'insights',
                                            })}
                                        </Text>
                                        <Text size="sm" color="neutral-500">
                                            {t(
                                                hasCategory
                                                    ? 'outcome_response__NO_NEED__description'
                                                    : 'outcome_response__NO_NEED__description_without_category',
                                                { ns: 'insights' },
                                            )}
                                        </Text>
                                    </div>
                                </button>
                            </div>
                        )}
                    />
                </div>
                <div className="flex flex-col gap-3 pt-3">
                    <Text size="sm" weight="medium" color="neutral-700">
                        {t('outcome_context__label', { ns: 'insights' })}
                    </Text>

                    <Controller
                        name="textRich"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <RichTextInput
                                trackingContext={{ source: 'sales_insight_outcome' }}
                                placeholder={getPlaceholder()}
                                value={value ?? undefined}
                                onChange={onChange}
                            />
                        )}
                    />
                </div>
            </DialogBody>
            <DialogFooter>
                <Button variant="secondary" onClick={onClose}>
                    {t('outcome_modal__cancel', { ns: 'insights' })}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isValid || isSubmitting}
                >
                    {isSubmitting && <Spinner />}
                    {t('outcome_modal__submit', { ns: 'insights' })}
                </Button>
            </DialogFooter>
        </>
    );
}
